/**
 * Continuous camera QR scanner (rear camera). @zxing/browser works on iOS
 * Safari too, where the native BarcodeDetector API is missing. Requires HTTPS
 * (or localhost) for camera access.
 */

import { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser';
import { CameraOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cameraErrorMessage } from '../cameraErrors';


interface QrScannerProps {
  onScan: (text: string) => void;
  paused?: boolean;
  /** Ignore the same code for this long (badge held in front of the camera) */
  repeatDelayMs?: number;
}

export function QrScanner({ onScan, paused = false, repeatDelayMs = 4000 }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastRef = useRef<{ text: string; at: number } | null>(null);
  const onScanRef = useRef(onScan);
  const pausedRef = useRef(paused);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  onScanRef.current = onScan;
  pausedRef.current = paused;

  useEffect(() => {
    let controls: IScannerControls | undefined;
    let cancelled = false;
    const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 150 });

    reader
      .decodeFromConstraints({ video: { facingMode: 'environment' } }, videoRef.current ?? undefined, (result) => {
        if (!result || pausedRef.current) return;
        const text = result.getText();
        const now = Date.now();
        if (lastRef.current && lastRef.current.text === text && now - lastRef.current.at < repeatDelayMs) return;
        lastRef.current = { text, at: now };
        onScanRef.current(text);
      })
      .then((c) => {
        if (cancelled) c.stop();
        else controls = c;
      })
      .catch((err: unknown) => {
        console.error('QR scanner camera error', err);
        if (!cancelled) setError(cameraErrorMessage(err));
      });

    return () => {
      cancelled = true;
      controls?.stop();
    };
  }, [repeatDelayMs, attempt]);

  if (error) {
    return (
      <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-lg bg-muted p-6 text-center text-sm text-muted-foreground">
        <CameraOff className="size-8" />
        {error}
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setError(null);
            setAttempt((n) => n + 1);
          }}
        >
          <RefreshCw className="mr-2 size-4" /> Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-lg bg-black">
      <video ref={videoRef} className="size-full object-cover" muted playsInline />
      {/* Aim box */}
      <div className="pointer-events-none absolute inset-[18%] rounded-lg border-4 border-white/80" />
      {paused && <div className="absolute inset-0 bg-black/50" />}
    </div>
  );
}
